import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, User, Mail, Phone, Briefcase, Users, Calendar,
  Send, Check, MessageSquare, GraduationCap, Target
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import Layout from '../layout/Layout';
import { contactAPI } from '../../lib/api';

const DevisPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    type: 'adulte', // 'adulte' ou 'entreprise'
    // Infos personnelles / entreprise
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    entreprise: '',
    fonction: '',
    // Infos formation
    nombre_personnes: '1',
    niveau_actuel: '',
    objectif: '',
    disponibilites: '',
    financement: 'cpf', // 'cpf', 'opco', 'personnel', 'autre'
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await contactAPI.sendDevis(formData);
      setSubmitted(true);
      toast({ 
        title: 'Demande envoyée !', 
        description: 'Nous vous recontacterons dans les 24h.'
      });
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: 'Une erreur est survenue. Veuillez réessayer.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl"
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#2C3C65] mb-4">Demande envoyée !</h2>
            <p className="text-[#6777A1] mb-6">
              Merci pour votre demande de devis. Notre équipe vous recontactera dans les 24 heures 
              pour discuter de vos besoins et vous proposer une solution adaptée.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-[#285BBB] text-white font-semibold rounded-lg hover:bg-[#1e4a9a] transition-colors"
            >
              Retour à l'accueil
            </Link>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] py-16">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <span className="inline-block px-4 py-2 bg-[#ff3a2d] rounded-full text-white text-sm font-medium mb-4">
              Formations Adultes & Entreprises
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Demandez votre devis personnalisé
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Recevez une proposition adaptée à vos besoins et objectifs en moins de 24h
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Formulaire */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Type de demande */}
                  <div>
                    <h3 className="text-lg font-semibold text-[#2C3C65] mb-4">Type de formation</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, type: 'adulte' })}
                        className={`p-6 rounded-xl border-2 text-left transition-all ${
                          formData.type === 'adulte'
                            ? 'border-[#285BBB] bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <User className={`h-8 w-8 mb-3 ${formData.type === 'adulte' ? 'text-[#285BBB]' : 'text-gray-400'}`} />
                        <h4 className="font-semibold text-[#2C3C65]">Formation individuelle</h4>
                        <p className="text-sm text-[#6777A1] mt-1">Cours particuliers ou collectifs</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, type: 'entreprise' })}
                        className={`p-6 rounded-xl border-2 text-left transition-all ${
                          formData.type === 'entreprise'
                            ? 'border-[#285BBB] bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Building2 className={`h-8 w-8 mb-3 ${formData.type === 'entreprise' ? 'text-[#285BBB]' : 'text-gray-400'}`} />
                        <h4 className="font-semibold text-[#2C3C65]">Formation entreprise</h4>
                        <p className="text-sm text-[#6777A1] mt-1">Pour vos équipes</p>
                      </button>
                    </div>
                  </div>

                  {/* Informations de contact */}
                  <div>
                    <h3 className="text-lg font-semibold text-[#2C3C65] mb-4">Vos coordonnées</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                        <Input
                          required
                          value={formData.nom}
                          onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                          placeholder="Votre nom"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                        <Input
                          required
                          value={formData.prenom}
                          onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                          placeholder="Votre prénom"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <Input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="votre@email.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                        <Input
                          type="tel"
                          required
                          value={formData.telephone}
                          onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                          placeholder="06 XX XX XX XX"
                        />
                      </div>
                      {formData.type === 'entreprise' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise *</label>
                            <Input
                              required
                              value={formData.entreprise}
                              onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                              placeholder="Nom de l'entreprise"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Fonction</label>
                            <Input
                              value={formData.fonction}
                              onChange={(e) => setFormData({ ...formData, fonction: e.target.value })}
                              placeholder="Votre poste"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Détails de la formation */}
                  <div>
                    <h3 className="text-lg font-semibold text-[#2C3C65] mb-4">Votre projet de formation</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nombre de personnes à former
                        </label>
                        <select
                          className="w-full p-3 border rounded-lg"
                          value={formData.nombre_personnes}
                          onChange={(e) => setFormData({ ...formData, nombre_personnes: e.target.value })}
                        >
                          <option value="1">1 personne</option>
                          <option value="2-5">2 à 5 personnes</option>
                          <option value="6-10">6 à 10 personnes</option>
                          <option value="10+">Plus de 10 personnes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Niveau actuel estimé
                        </label>
                        <select
                          className="w-full p-3 border rounded-lg"
                          value={formData.niveau_actuel}
                          onChange={(e) => setFormData({ ...formData, niveau_actuel: e.target.value })}
                        >
                          <option value="">Sélectionnez...</option>
                          <option value="debutant">Débutant (A1-A2)</option>
                          <option value="intermediaire">Intermédiaire (B1)</option>
                          <option value="avance">Avancé (B2)</option>
                          <option value="expert">Expert (C1-C2)</option>
                          <option value="inconnu">Je ne sais pas</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Objectif principal
                        </label>
                        <select
                          className="w-full p-3 border rounded-lg"
                          value={formData.objectif}
                          onChange={(e) => setFormData({ ...formData, objectif: e.target.value })}
                        >
                          <option value="">Sélectionnez...</option>
                          <option value="certification">Préparer une certification Cambridge</option>
                          <option value="professionnel">Améliorer mon anglais professionnel</option>
                          <option value="communication">Développer l'aisance à l'oral</option>
                          <option value="voyage">Voyager / Mobilité internationale</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Disponibilités préférées
                        </label>
                        <Input
                          value={formData.disponibilites}
                          onChange={(e) => setFormData({ ...formData, disponibilites: e.target.value })}
                          placeholder="Ex: Soirs en semaine, Samedi matin..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mode de financement envisagé
                        </label>
                        <select
                          className="w-full p-3 border rounded-lg"
                          value={formData.financement}
                          onChange={(e) => setFormData({ ...formData, financement: e.target.value })}
                        >
                          <option value="cpf">Mon Compte CPF</option>
                          <option value="opco">OPCO / Plan de formation entreprise</option>
                          <option value="personnel">Financement personnel</option>
                          <option value="autre">Autre / Je ne sais pas</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Précisions complémentaires
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Décrivez votre projet, vos contraintes, vos questions..."
                      rows={4}
                    />
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-[#ff3a2d] hover:bg-[#e6352a] text-white font-bold text-lg"
                  >
                    {loading ? (
                      'Envoi en cours...'
                    ) : (
                      <>
                        <Send className="h-5 w-5 mr-2" />
                        Envoyer ma demande de devis
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Avantages */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-lg font-semibold text-[#2C3C65] mb-4">Pourquoi nous choisir ?</h3>
                <ul className="space-y-4">
                  {[
                    { icon: GraduationCap, text: 'Centre agréé Cambridge' },
                    { icon: Target, text: 'Formations 100% éligibles CPF' },
                    { icon: Users, text: 'Formateurs natifs' },
                    { icon: Briefcase, text: 'Certifié Qualiopi' },
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#F5F7FF] rounded-lg flex items-center justify-center">
                        <item.icon className="h-5 w-5 text-[#285BBB]" />
                      </div>
                      <span className="text-[#2C3C65] font-medium">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact rapide */}
              <div className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] rounded-2xl shadow-xl p-6 text-white">
                <h3 className="text-lg font-semibold mb-4">Besoin d'un contact rapide ?</h3>
                <p className="text-blue-100 text-sm mb-4">
                  Notre équipe est disponible pour répondre à vos questions
                </p>
                <div className="space-y-3">
                  <a 
                    href="tel:+33626716022"
                    className="flex items-center gap-3 text-white hover:text-blue-200 transition-colors"
                  >
                    <Phone className="h-5 w-5" />
                    <span>06 26 71 60 22</span>
                  </a>
                </div>
              </div>

              {/* Témoignage */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <MessageSquare className="h-8 w-8 text-[#285BBB] mb-4" />
                <blockquote className="text-[#6777A1] italic mb-4">
                  "Formation excellente ! J'ai pu passer de B1 à B2 en 6 mois grâce à un accompagnement personnalisé."
                </blockquote>
                <p className="text-sm font-medium text-[#2C3C65]">— Sophie M., Cadre RH</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DevisPage;
